import { useState, useEffect } from "react";
import { BedDouble, X, Loader } from "lucide-react";
import { mastersApi, eventsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface Room {
  id: number;
  name: string;
  type: string;
  capacity: number;
  rate: number;
  status: string;
}

interface AddRoomBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  onBookingAdded: () => void;
}

export default function AddRoomBookingModal({ isOpen, onClose, eventId, onBookingAdded }: AddRoomBookingModalProps) {
  const { showToast } = useToast();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    room_id: "",
    guest_name: "",
    guest_count: 1,
    check_in: "",
    check_out: "",
    rate: "",
    notes: "",
  });

  // Computed nights and total
  const nights = form.check_in && form.check_out
    ? Math.max(1, Math.ceil((new Date(form.check_out).getTime() - new Date(form.check_in).getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  const total = nights > 0 && form.rate ? nights * parseFloat(form.rate) : 0;

  useEffect(() => {
    if (!isOpen) return;
    setLoadingRooms(true);
    mastersApi.rooms.list()
      .then((res: any) => {
        const list = res?.data?.data || res?.data || res || [];
        setRooms(Array.isArray(list) ? list : []);
      })
      .catch(() => showToast("Failed to load rooms", "error"))
      .finally(() => setLoadingRooms(false));
  }, [isOpen]);

  const handleRoomChange = (roomId: string) => {
    const room = rooms.find((r) => String(r.id) === roomId);
    setForm((f) => ({ ...f, room_id: roomId, rate: room ? String(room.rate) : "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.room_id) { showToast("Please select a room", "error"); return; }
    if (!form.guest_name.trim()) { showToast("Guest name is required", "error"); return; }
    if (!form.check_in || !form.check_out) { showToast("Check-in and check-out dates are required", "error"); return; }
    if (new Date(form.check_out) <= new Date(form.check_in)) { showToast("Check-out must be after check-in", "error"); return; }
    if (!form.rate || parseFloat(form.rate) < 0) { showToast("Please enter a valid rate", "error"); return; }

    setSubmitting(true);
    try {
      await eventsApi.addRoomBooking(eventId, {
        room_id: parseInt(form.room_id),
        guest_name: form.guest_name.trim(),
        guest_count: form.guest_count,
        check_in: form.check_in,
        check_out: form.check_out,
        rate: parseFloat(form.rate),
        notes: form.notes || null,
      });
      showToast("Room booked successfully", "success");
      resetForm();
      onBookingAdded();
    } catch (err: any) {
      showToast(err?.message || "Failed to book room", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ room_id: "", guest_name: "", guest_count: 1, check_in: "", check_out: "", rate: "", notes: "" });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <BedDouble className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-stone-900">Book a Room</h2>
              <p className="text-xs text-stone-500">Assign a room to this event</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-stone-100 transition-colors">
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Room selection */}
          <div>
            <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Room *</label>
            {loadingRooms ? (
              <div className="flex items-center gap-2 text-stone-400 text-sm py-2">
                <Loader className="w-4 h-4 animate-spin" /> Loading rooms...
              </div>
            ) : (
              <select
                value={form.room_id}
                onChange={(e) => handleRoomChange(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                required
              >
                <option value="">Select a room...</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name} — {room.type} · Cap: {room.capacity} · ₹{room.rate}/night
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Guest name + count */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Guest Name *</label>
              <input
                type="text"
                value={form.guest_name}
                onChange={(e) => setForm((f) => ({ ...f, guest_name: e.target.value }))}
                placeholder="Guest name"
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Guests *</label>
              <input
                type="number"
                min={1}
                value={form.guest_count}
                onChange={(e) => setForm((f) => ({ ...f, guest_count: parseInt(e.target.value) || 1 }))}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Check-in / Check-out */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Check-in *</label>
              <input
                type="date"
                value={form.check_in}
                onChange={(e) => setForm((f) => ({ ...f, check_in: e.target.value }))}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Check-out *</label>
              <input
                type="date"
                value={form.check_out}
                min={form.check_in || undefined}
                onChange={(e) => setForm((f) => ({ ...f, check_out: e.target.value }))}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Rate */}
          <div>
            <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Rate per Night (₹) *</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.rate}
              onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
              placeholder="0.00"
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              required
            />
          </div>

          {/* Computed summary */}
          {nights > 0 && total > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-amber-700">
                {nights} night{nights !== 1 ? "s" : ""} × ₹{parseFloat(form.rate || "0").toLocaleString("en-IN")}
              </span>
              <span className="text-base font-bold text-amber-800">
                ₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Optional notes..."
              className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? <><Loader className="w-4 h-4 animate-spin" /> Booking...</> : "Book Room"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
